import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import type { StarterData, FeedingEntry } from '@/types/starter';
import type { Schedule } from '@/types/schedule';
import type { WizardState } from '@/types/wizard';
import type { AppSettings } from '@/types/common';

interface YeastusDBSchema extends DBSchema {
  starter: {
    key: string;
    value: StarterData;
  };
  feedingHistory: {
    key: string;
    value: FeedingEntry;
    indexes: {
      'by-date': string;
      'by-starter': string;
    };
  };
  schedules: {
    key: string;
    value: Schedule;
    indexes: {
      'by-bakeDate': string;
      'by-archived': string;
    };
  };
  wizard: {
    key: string;
    value: WizardState;
  };
  settings: {
    key: string;
    value: AppSettings;
  };
}

const DB_NAME = 'yeastus-db';
const DB_VERSION = 2; // v3 PRD: schema v2

let dbInstance: IDBPDatabase<YeastusDBSchema> | null = null;

export async function initDB(): Promise<IDBPDatabase<YeastusDBSchema>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<YeastusDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        // Create initial stores
        db.createObjectStore('starter', { keyPath: 'id' });

        const feedingStore = db.createObjectStore('feedingHistory', { keyPath: 'id' });
        feedingStore.createIndex('by-date', 'date');
        feedingStore.createIndex('by-starter', 'starterId');

        const scheduleStore = db.createObjectStore('schedules', { keyPath: 'id' });
        scheduleStore.createIndex('by-bakeDate', 'bakeDate');
        scheduleStore.createIndex('by-archived', 'archived');

        db.createObjectStore('wizard', { keyPath: 'startDate' });
      }

      if (oldVersion < 2) {
        // v3 additions: settings store, syncStatus/source fields on existing records
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { keyPath: 'reminderTime' });
        }
      }
    },
  });

  return dbInstance;
}

export async function closeDB(): Promise<void> {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

// Error handling utilities
export function isQuotaExceededError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.code === 22 || // Legacy
      error.code === 1014 || // Firefox
      error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

export async function isPrivateBrowsing(): Promise<boolean> {
  try {
    const db = await openDB('__private_test__', 1, {
      upgrade(db) {
        db.createObjectStore('test');
      },
    });
    db.close();
    const deleteReq = indexedDB.deleteDatabase('__private_test__');
    await new Promise<void>((resolve) => {
      deleteReq.onsuccess = () => resolve();
      deleteReq.onerror = () => resolve();
    });
    return false;
  } catch {
    return true;
  }
}

export type { YeastusDBSchema };
